import { QueryResult } from '@apollo/client'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  EditDesignSubmittalInput,
  GetDesignSubmittalsMetadataQuery,
  GetDesignSubmittalsQuery,
  useEditDesignSubmittalMutation,
  useGetDesignSubmittalsMetadataQuery,
  useGetDesignSubmittalsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission, SharePointParentFolder } from 'shared'
import {
  useCIMSModalValues,
  UseCIMSModalValues,
} from 'components/CIMSModal/hooks/useCIMSModal'
import { CreateDesignSubmittalModalData } from './components/DesignSubmittalLogTable/hooks/useCreateOptions/components/CreateDesignSubmittalModal'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { UseSharePointExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks/types'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'
import { simpleEditNotification } from '../../../../../../../hooks/notificationHandler'

type GetDesignSubmittalsQueryResult = QueryResult<GetDesignSubmittalsQuery>
export type DesignSubmittal = NonNullable<
  GetDesignSubmittalsQueryResult['data']
>['designSubmittals']['records'][0]

type GetDesignSubmittalsMetadataQueryResult =
  QueryResult<GetDesignSubmittalsMetadataQuery>

const { Provider, useContextFn } = createStrictContext<
  WithPayItemGlobalId &
    UseSharePointExternalLinks<DesignSubmittal> & {
      records: DesignSubmittal[]
      refetch: GetDesignSubmittalsQueryResult['refetch']
      fetching: GetDesignSubmittalsQueryResult['loading']
      allPackageNumbers: string[]
      metaFetching: GetDesignSubmittalsMetadataQueryResult['loading']
      edit: (input: EditDesignSubmittalInput) => Promise<void>
      advancementModal: UseCIMSModalValues<DesignSubmittal>
      revertModal: UseCIMSModalValues<DesignSubmittal>
      createModal: UseCIMSModalValues<CreateDesignSubmittalModalData>
      selectOptions: {
        actionsOptions: DefaultOptionType[]
        disciplinesOptions: DefaultOptionType[]
        fdcOptions: DefaultOptionType[]
        orgsOptions: DefaultOptionType[]
        typesOptions: DefaultOptionType[]
      }
      quickLinks?: NonNullable<
        GetDesignSubmittalsMetadataQueryResult['data']
      >['project']['dsQuickLinks']
      permissions: {
        canDisposition: boolean
        canNumberName: boolean
        canReviewAll: boolean
        canRoute: boolean
      }
    }
>('DesignSubmittalLogContext')

export const useDesignSubmittalLogContext = useContextFn

export const DesignSubmittalLogProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    refetch,
    loading: fetching,
  } = useGetDesignSubmittalsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      projectId: projectId!,
      input: { projectId: { equals: projectId } },
    },
  })
  const { data: metadata, loading: metaFetching } =
    useGetDesignSubmittalsMetadataQuery({
      ...DefaultQueryHookOptions,
      skip: !projectId,
      variables: { globalId: projectId! },
    })
  const [edit] = useEditDesignSubmittalMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editDesignSubmittal: {
        designSubmittal: { sharePointFolderName },
      },
    }) => simpleEditNotification(sharePointFolderName),
  })

  const advancementModal = useCIMSModalValues<DesignSubmittal>()
  const revertModal = useCIMSModalValues<DesignSubmittal>()
  const createModal = useCIMSModalValues<CreateDesignSubmittalModalData>()

  const actionsOptions = useOptions({
    items: metadata?.project?.dsActions,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })
  const disciplinesOptions = useOptions({
    items: metadata?.project?.dsDisciplines,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })
  const fdcOptions = useOptions({
    items: metadata?.project?.fieldDesignChanges,
    value: 'id',
    label: 'formattedNumber',
    sort: 'formattedNumber',
  })
  const orgsOptions = useOptions({
    items: metadata?.project?.dsOrganizations,
    value: 'id',
    label: 'name',
    tooltip: 'description',
    sort: 'name',
  })
  const typesOptions = useOptions({
    items: metadata?.project?.dsTypes,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })

  const canDisposition = useCheckPermission(
    Permission.GiveDispositionDesignSubmittal
  )
  const canNumberName = useCheckPermission(
    Permission.EditNumberNameDesignSubmittal
  )
  const canReviewAll = useCheckPermission(Permission.ReviewAllDesignSubmittal)
  const canRoute = useCheckPermission(Permission.RouteDesignSubmittal)
  const externalLinks = useExternalLinks<DesignSubmittal>({
    type: 'sharepoint',
    dataFn: ({
      id,
      externalLinks,
      sharePointResourceWebUrl,
      sharePointFolderName,
    }) => ({
      recordId: id,
      externalLinks,
      folder: sharePointFolderName,
      resource: { webUrl: sharePointResourceWebUrl },
    }),
    parent: SharePointParentFolder.DesignSubmittals,
    refetch,
  })
  return (
    <Provider
      value={{
        ...externalLinks,
        payItemGlobalId,
        records: data?.designSubmittals?.records ?? [],
        refetch,
        fetching,
        edit: async (input) => {
          await edit({ variables: { input } })
        },
        allPackageNumbers:
          data?.designSubmittals?.records?.map(({ packageNumber }) =>
            packageNumber.trim()
          ) ?? [],
        metaFetching,
        advancementModal,
        revertModal,
        createModal,
        selectOptions: {
          actionsOptions,
          disciplinesOptions,
          fdcOptions,
          orgsOptions,
          typesOptions,
        },
        permissions: {
          canDisposition,
          canNumberName,
          canReviewAll,
          canRoute,
        },
        quickLinks: metadata?.project?.dsQuickLinks,
      }}
    >
      {children}
    </Provider>
  )
}
